import React from 'react';
import { Button, ButtonVariant } from '@/shared/components/Buttons';
import {
  Card,
  CardBody,
  CardContent,
  CardStyle,
} from '@/shared/components/Card';
import { Dialog } from '@/shared/components/Dialog';

// ============================================================================
// Types
// ============================================================================

export interface UpdateStatusDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Dialog title (header) */
  title: string;
  /** Body content - string or React node */
  content: React.ReactNode;
  /** Confirm button label */
  confirmButtonLabel: string;
  /** Confirm button label when submitting (optional; falls back to confirmButtonLabel) */
  confirmButtonLabelLoading?: string;
  /** Cancel button label (optional; default "Anuluj") */
  cancelButtonLabel?: string;
  /** Optional icon shown above the content (e.g. activate/deactivate icon) */
  icon?: React.ReactNode;
  /** Confirm button variant (optional; default Error) */
  confirmButtonVariant?: ButtonVariant;
  isSubmitting?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const UpdateStatusDialog: React.FC<UpdateStatusDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  content,
  confirmButtonLabel,
  confirmButtonLabelLoading,
  cancelButtonLabel = 'Anuluj',
  icon,
  confirmButtonVariant = ButtonVariant.Error,
  isSubmitting = false,
}) => {
  const confirmLabel =
    isSubmitting && confirmButtonLabelLoading
      ? confirmButtonLabelLoading
      : confirmButtonLabel;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      maxWidth={432}
    >
      <Card style={CardStyle.Elevated} width={432}>
        <CardBody padding="large">
          <div className="flex flex-col items-center gap-6 text-center w-full min-w-0">
            {icon != null && (
              <div className="flex items-center justify-center w-[137px] h-[137px] shrink-0">
                {icon}
              </div>
            )}

            <CardContent
              title={title}
              className="!px-0 !py-0 flex flex-col items-center !gap-4 !text-[#474A4C] [&>p]:!font-normal w-full min-w-0 max-w-full"
            >
              <p
                className="text-center w-full min-w-0 break-words"
                style={{
                  fontSize: 14,
                  lineHeight: '20px',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                {content}
              </p>
            </CardContent>

            <div className="flex flex-row items-center justify-center w-full gap-3 px-5">
              <Button
                label={cancelButtonLabel}
                onClick={onClose}
                disabled={isSubmitting}
                customFontWeight={500}
                className={`!bg-transparent !h-[40px] !rounded-[8px] border !text-labelLarge !text-on-surface-variant hover:!bg-neutral-95 hover:border-neutral-70 w-full ${
                  isSubmitting
                    ? '!bg-[#181A1B1A] !border-[#DCDDE0] opacity-38 !text-[#181A1B] !cursor-not-allowed'
                    : '!border-[#DCDDE0] cursor-pointer'
                }`}
              />
              <Button
                label={confirmLabel}
                variant={confirmButtonVariant}
                onClick={onConfirm}
                disabled={isSubmitting}
                customFontWeight={500}
                className="!h-[40px] !rounded-[8px] !text-labelLarge !text-white w-full cursor-pointer"
              />
            </div>
          </div>
        </CardBody>
      </Card>
    </Dialog>
  );
};

export default UpdateStatusDialog;
